import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Business,
  People,
} from '@mui/icons-material';
import { apiClient } from '../services/api';
import { Department, User } from '../types';

interface DepartmentFormData {
  name: string;
  description: string;
  isActive: boolean;
}

const DepartmentsManagementPage: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [formData, setFormData] = useState<DepartmentFormData>({
    name: '',
    description: '',
    isActive: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState<
    'name' | 'description' | 'employees' | 'status' | 'created'
  >('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');


  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [departmentsData, usersData] = await Promise.all([
        apiClient.departments.getAll(),
        apiClient.users.getAll(),
      ]);
      setDepartments(departmentsData.data.data);
      setUsers(usersData.data.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (department?: Department) => {
    if (department) {
      setEditingDepartment(department);
      setFormData({
        name: department.name,
        description: department.description || '',
        isActive: department.isActive,
      });
    } else {
      setEditingDepartment(null);
      setFormData({
        name: '',
        description: '',
        isActive: true,
      });
    }
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDepartment(null);
    setError(null);
    setSuccess(null);
    setSubmitting(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingDepartment) {
        await apiClient.departments.update(editingDepartment.id, formData);
        setSuccess('Department updated successfully');
      } else {
        await apiClient.departments.create(formData);
        setSuccess('Department created successfully');
      }

      // Close dialog immediately and reload data in background
      handleCloseDialog();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save department');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (departmentId: string) => {
    if (window.confirm('Are you sure you want to delete this department? This will affect all users in this department.')) {
      try {
        await apiClient.departments.delete(departmentId);
        setSuccess('Department deleted successfully');
        await loadData();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete department');
      }
    }
  };

  const getUsersInDepartment = (departmentId: string) => {
    return users.filter(user => user.departmentId === departmentId).length;
  };

  const getSortValue = (department: Department, key: typeof sortBy) => {
    switch (key) {
      case 'name':
        return department.name || '';
      case 'description':
        return department.description || '';
      case 'employees':
        return getUsersInDepartment(department.id).toString();
      case 'status':
        return department.isActive ? 'active' : 'inactive';
      case 'created':
        return department.createdAt || '';
      default:
        return '';
    }
  };

  const handleSortChange = (key: typeof sortBy) => {
    if (sortBy === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDirection('asc');
  };

  const sortedDepartments = [...departments]
    .map((department, index) => ({ department, index }))
    .sort((a, b) => {
      const aValue = getSortValue(a.department, sortBy);
      const bValue = getSortValue(b.department, sortBy);
      const comparison = aValue.localeCompare(bValue, undefined, { sensitivity: 'base', numeric: true });
      if (comparison !== 0) {
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return a.index - b.index;
    })
    .map(({ department }) => department);


  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
          Departments Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Department
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}


      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortBy === 'name' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'description' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'description'}
                      direction={sortBy === 'description' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('description')}
                    >
                      Description
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'employees' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'employees'}
                      direction={sortBy === 'employees' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('employees')}
                    >
                      Employees
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'status' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'created' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'created'}
                      direction={sortBy === 'created' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('created')}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedDepartments.map((department) => (
                  <TableRow key={department.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Business sx={{ mr: 1, fontSize: 16 }} />
                        {department.name}
                      </Box>
                    </TableCell>
                    <TableCell>{department.description || 'No description'}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <People sx={{ mr: 1, fontSize: 16 }} />
                        {getUsersInDepartment(department.id)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={department.isActive ? 'Active' : 'Inactive'}
                        color={department.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(department.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(department)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(department.id)}
                        color="error"
                        disabled={getUsersInDepartment(department.id) > 0}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Department Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDepartment ? 'Edit Department' : 'Add New Department'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Department Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting ? 'Saving...' : (editingDepartment ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default DepartmentsManagementPage;
